import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import { firstValueFrom, take } from 'rxjs';
import { ProceduresService } from '../../../../core/services/procedures.service';
import { TranslateModule } from '@ngx-translate/core';
import { InputMaskModule } from 'primeng/inputmask';
import { PhoneInputComponent } from '../../../../shared/components/phone-input/phone-input.component';
import { AppButton } from '../../../../shared/components/app-button/app-button';

@Component({
  selector: 'app-add-health-plan-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    InputMaskModule,
    AppButton,
    PhoneInputComponent,
  ],
  templateUrl: './add-health-plan-modal.html',
})
export class AddHealthPlanModalComponent implements OnInit, OnChanges {
  @Input() isOpen = false;
  @Input() branchId: number | null = null;
  @Input() healthPlan: any | null = null; // Add input for editing
  @Output() close = new EventEmitter<void>();
  @Output() planAdded = new EventEmitter<void>(); // Renaming strictly might break parent binding if not careful, but let's keep it or add planSaved
  @Output() planSaved = new EventEmitter<void>();

  form: FormGroup;
  loading = false;

  countries = [
    { code: 'PT', name: 'Portugal' },
    { code: 'US', name: 'United States' },
    { code: 'BR', name: 'Brazil' },
    // Add more as needed
  ];

  constructor(
    private fb: FormBuilder,
    private proceduresService: ProceduresService,
    private store: Store,
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      cell_phone: [null],
      email: ['', [Validators.required, Validators.email]],
      nipc: ['', Validators.required],
      country: ['PT'],
      postal_code: ['', Validators.required],
      address: ['', Validators.required],
      address_number: ['', Validators.required], // Start empty
      address_complement: [''],
      neighborhood: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      active: [true], // For edit mode
    });
  }

  ngOnInit() {
    // If we need to fetch branchId from store if not passed input:
    // But parent passes it.
    if (!this.branchId) {
      this.store
        .select(selectUserInfo)
        .pipe(take(1))
        .subscribe((info) => {
          if (info) this.branchId = info.branches_id || info.branchId;
        });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['healthPlan'] && this.healthPlan) {
      // Editing mode
      this.form.patchValue({
        name: this.healthPlan.name,
        cell_phone: this.healthPlan.cell_phone,
        email: this.healthPlan.email,
        nipc: this.healthPlan.nipc,
        country: this.healthPlan.country || 'PT',
        postal_code: this.healthPlan.postal_code,
        address: this.healthPlan.address,
        address_number: this.healthPlan.address_number,
        address_complement: this.healthPlan.address_complement,
        neighborhood: this.healthPlan.neighborhood,
        city: this.healthPlan.city,
        state: this.healthPlan.state,
        active: this.healthPlan.active,
      });
    } else if (changes['isOpen'] && this.isOpen && !this.healthPlan) {
      // Reset if opening in add mode
      this.form.reset({ country: 'PT', active: true });
    }
  }

  onClose() {
    this.close.emit();
    this.form.reset({ country: 'PT', active: true });
  }

  async onSubmit() {
    if (this.form.invalid) return;

    this.loading = true;
    try {
      const formValue = this.form.value;

      const payload = {
        ...formValue,
        nipc: formValue.nipc ? Number(formValue.nipc) : null,
        branches_id: this.branchId,
        // Ensure validation fields are correct types if needed
      };

      if (this.healthPlan && this.healthPlan.id) {
        // Update
        const { error } = await this.proceduresService.updateHealthPlan(
          this.healthPlan.id,
          payload,
        );
        if (error) throw error;
      } else {
        // Create
        const { error } = await this.proceduresService.createHealthPlan(payload);
        if (error) throw error;
      }

      this.planSaved.emit();
      this.planAdded.emit(); // Keep for backward compatibility if parent uses it
      this.onClose();
    } catch (error) {
      console.error('Error saving health plan:', error);
      // Handle error (show toast)
    } finally {
      this.loading = false;
    }
  }
}
